import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPencil,
  faTrash,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';

import { api } from '@/lib/axios';
import { API_ROUTES, ROUTES } from '@/constants';
import { Button, Card, ImageLightbox, Spinner } from '@/components/ui';
import { GenerationProgress } from '@/components/GenerationProgress';
import type { PresentationItem } from '@/types/presentation';
import styles from './InvitationDetail.module.css';

const IMAGE_LOADING_LABELS = [
  'Requesting generation',
  'Considering prompt',
  'Adding style elements',
  'Finalizing',
];

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  generating: 'Generating...',
  completed: 'Completed',
  failed: 'Failed',
};

export function InvitationDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [imgLabel, setImgLabel] = useState(0);

  const { data: item, isLoading } = useQuery({
    queryKey: ['invitation-detail', id],
    queryFn: async () => {
      const res = await api.get<PresentationItem>(`${API_ROUTES.presentations}${id}/`);
      return res.data;
    },
    enabled: Boolean(id),
    refetchInterval: (query) => (query.state.data?.status === 'generating' ? 4000 : false),
  });

  const regenerateMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<PresentationItem>(`${API_ROUTES.presentations}${id}/generate/`, {
        regenerate: true,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitation-detail', id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`${API_ROUTES.presentations}${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      navigate(ROUTES.dashboardInvitations);
    },
  });

  useEffect(() => {
    if (item?.status !== 'generating') return;
    const id = setInterval(() => setImgLabel((s) => (s + 1) % IMAGE_LOADING_LABELS.length), 2200);
    return () => clearInterval(id);
  }, [item?.status]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <Spinner />
      </div>
    );
  }

  if (!item) return null;

  const ctx = (item.context_data ?? {}) as Record<string, unknown>;

  const allImages: string[] = [];
  if (item.invitation_text) allImages.push(item.invitation_text);
  if (item.generated_images?.length) {
    for (const url of item.generated_images) {
      if (!allImages.includes(url)) allImages.push(url);
    }
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Button variant="secondary" onClick={() => navigate(ROUTES.dashboardInvitations)}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </Button>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{item.name}</h1>
          <span
            className={`${styles.statusBadge} ${styles[`status_${item.status}` as keyof typeof styles]}`}
          >
            {STATUS_LABELS[item.status] ?? item.status}
          </span>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="secondary"
            onClick={() => regenerateMutation.mutate()}
            disabled={regenerateMutation.isPending || item.status === 'generating'}
          >
            <FontAwesomeIcon icon={faWandMagicSparkles} />
            {regenerateMutation.isPending ? ' Regenerating...' : ' Regenerate'}
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              navigate(ROUTES.dashboardInvitationEdit.replace(':id', String(item.id)))
            }
          >
            <FontAwesomeIcon icon={faPencil} /> Edit
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              if (window.confirm('Delete this invitation?')) {
                deleteMutation.mutate();
              }
            }}
            disabled={deleteMutation.isPending}
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </div>
      </div>

      {/* Generating state */}
      {item.status === 'generating' && (
        <div className={styles.generatingBanner}>
          {item.generation_progress ? (
            <GenerationProgress progress={item.generation_progress} mode="detailed" />
          ) : (
            <>
              <Spinner />
              <span>Generating invitation... page will refresh automatically.</span>
            </>
          )}
        </div>
      )}

      {/* Main content */}
      <div className={styles.mainGrid}>
        {/* Gallery */}
        <div className={styles.galleryCol}>
          <h2 className={styles.colTitle}>
            Invitation Cards {allImages.length > 0 && `(${allImages.length})`}
          </h2>
          <div className={styles.gallery}>
            {allImages.map((url, i) => (
              <button
                key={url}
                type="button"
                className={styles.imageWrapper}
                onClick={() => setLightboxIndex(i)}
                aria-label={`Open invitation ${i + 1}`}
              >
                <img
                  src={url}
                  alt={`Invitation ${i + 1}`}
                  className={styles.invitationImage}
                />
              </button>
            ))}
            {item.status === 'generating' &&
              (() => {
                const totalExpected = item.generation_progress?.total_images ?? 3;
                const pendingCount = Math.max(0, totalExpected - allImages.length);
                return Array.from({ length: pendingCount }, (_, i) => {
                  const isActive = i === 0;
                  return (
                    <div key={`ph-${i}`} className={`${styles.imageWrapper} ${styles.imagePlaceholder}`}>
                      <div className={styles.placeholderContent}>
                        {isActive ? (
                          <>
                            <span className={styles.placeholderDot} />
                            <span className={styles.placeholderLabel}>
                              {IMAGE_LOADING_LABELS[imgLabel]}
                            </span>
                          </>
                        ) : (
                          <span className={styles.placeholderLabel}>Queued</span>
                        )}
                      </div>
                      <div className={styles.placeholderShimmer} />
                    </div>
                  );
                });
              })()}
            {allImages.length === 0 && item.status !== 'generating' && (
              <div className={styles.emptyGallery}>No invitation cards generated yet.</div>
            )}
          </div>
        </div>

        {/* Details sidebar */}
        <div className={styles.detailsCol}>
          <Card className={styles.infoCard}>
            <h2 className={styles.colTitle}>Event Details</h2>
            <div className={styles.infoGrid}>
              {typeof ctx.theme === 'string' && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Theme</span>
                  <span className={styles.infoValue}>{ctx.theme}</span>
                </div>
              )}
              {typeof ctx.eventType === 'string' && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Type</span>
                  <span className={styles.infoValue}>{ctx.eventType}</span>
                </div>
              )}
              {typeof ctx.eventDate === 'string' && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Date</span>
                  <span className={styles.infoValue}>{ctx.eventDate}</span>
                </div>
              )}
              {typeof ctx.eventTime === 'string' && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Time</span>
                  <span className={styles.infoValue}>{ctx.eventTime}</span>
                </div>
              )}
              {typeof ctx.location === 'string' && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Venue</span>
                  <span className={styles.infoValue}>{ctx.location}</span>
                </div>
              )}
              {typeof ctx.dressCode === 'string' && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Dress Code</span>
                  <span className={styles.infoValue}>{ctx.dressCode}</span>
                </div>
              )}
              {typeof ctx.primaryColor === 'string' && typeof ctx.secondaryColor === 'string' && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Colors</span>
                  <div className={styles.colorSwatches}>
                    <div
                      className={styles.colorSwatch}
                      style={{ background: ctx.primaryColor }}
                      title={ctx.primaryColor}
                    />
                    <div
                      className={styles.colorSwatch}
                      style={{ background: ctx.secondaryColor }}
                      title={ctx.secondaryColor}
                    />
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      <ImageLightbox
        images={allImages}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        downloadFilename={`${item.name}-invitation`}
      />
    </div>
  );
}
